/**
 * Regras de cluster Senior Fastchip — espelho de `FastchipClusterRules` (API).
 */
export const FASTCHIP_CLUSTER_HB = 'HB';
export const FASTCHIP_CLUSTER_CP = 'CP';
export const FASTCHIP_CLUSTER_TPCL = 'TPCL';
export const FASTCHIP_CLUSTER_BA = 'BA';
export const FASTCHIP_CLUSTER_HDM2 = 'HDM2';
/** Cluster Senior DH TELECOM — mercado RJ/MG/ES (por entrega; Fastchip só se `DH_FASTCHIP_ENABLED`). */
export const FASTCHIP_CLUSTER_DH = 'DH';

/**
 * RN — clusters E.R.2/DH do mercado RJ/MG/ES: administráveis via GettelWeb (ver
 * `useFastchipClusterSettings`), não mais hardcoded. Este módulo mantém um estado
 * em memória, populado por `setFastchipClusterSettings()` a partir de
 * `GET /api/fastchip-cluster-settings` — até a primeira carga, usa os valores
 * históricos (E.R.2 ligado, DH desligado) como fallback seguro.
 */
export type FastchipClusterSettingsState = {
  er2Enabled: boolean;
  dhEnabled: boolean;
  /** Cluster usado quando os dois estão habilitados e o time não tem preferência ("ER2" | "DH"). */
  defaultCluster: 'ER2' | 'DH';
};

let fastchipClusterSettingsState: FastchipClusterSettingsState = {
  er2Enabled: true,
  dhEnabled: false,
  defaultCluster: 'ER2',
};

/** Atualiza o estado em memória a partir da configuração vinda da API (ver `useFastchipClusterSettings`). */
export function setFastchipClusterSettings(settings: {
  er2Enabled: boolean;
  dhEnabled: boolean;
  defaultCluster: string;
}): void {
  fastchipClusterSettingsState = {
    er2Enabled: settings.er2Enabled,
    dhEnabled: settings.dhEnabled,
    defaultCluster: settings.defaultCluster?.trim().toUpperCase().includes('DH') ? 'DH' : 'ER2',
  };
}

export function getFastchipClusterSettings(): FastchipClusterSettingsState {
  return fastchipClusterSettingsState;
}

/** @deprecated Use `getFastchipClusterSettings().er2Enabled`. Mantido para compat de leitura. */
export function isEr2ClusterEnabled(): boolean {
  return fastchipClusterSettingsState.er2Enabled;
}

/** @deprecated Use `getFastchipClusterSettings().dhEnabled`. Mantido para compat de leitura. */
export function isDhFastchipEnabled(): boolean {
  return fastchipClusterSettingsState.dhEnabled;
}

const HB_DDDS = new Set(['81', '87', '85', '88']);
const CP_DDDS = new Set(['86', '89', '82', '84', '83']);
/** União HB ∪ CP (mercado Nordeste Fastchip — um cluster por contrato aditivo). */
const HB_CP_UNION_DDDS = new Set(['81', '82', '83', '84', '85', '86', '87', '88', '89']);

const PR_MH_CLUSTER_CITIES = new Set(
  [
    'GUARAPUAVA',
    'UNIAO DA VITORIA',
    'PRUDENTOPOLIS',
    'PITANGA',
    'LARANJEIRAS DO SUL',
    'PINHAO',
    'CRUZ MACHADO',
    'BITURUNA',
    'CANDOI',
    'PALMITAL',
    'GENERAL CARNEIRO',
    'TURVO',
    'CANTAGALO',
    'RIO BONITO DO IGUACU',
    'NOVA LARANJEIRAS',
    'SANTA MARIA DO OESTE',
    'GUAMIRANGA',
    'RESERVA DO IGUACU',
    'GOIOXIM',
    'BOA VENTURA DE SAO ROQUE',
    'LARANJAL',
    'FOZ DO JORDAO',
    'MARQUINHO',
    'PORTO VITORIA',
    'CAMPINA DO SIMAO',
    'VIRMOND',
    'MATO RICO',
    'PORTO BARREIRO',
  ].map((c) => c.toUpperCase())
);
const TPCL_DDDS = new Set(['61', '62', '63', '64', '65', '66', '67', '68', '69']);
const BA_DDDS = new Set(['71', '73', '74', '75', '77']);
const HDM2_DDDS = new Set(['51', '52', '53', '54', '55']);
const HB_MARKET_ESTADOS = new Set(['PE', 'CE']);
const CP_MARKET_ESTADOS = new Set(['PI', 'AL', 'RN', 'PB']);
const BA_MARKET_ESTADOS = new Set(['BA']);
const HDM2_MARKET_ESTADOS = new Set(['RS']);

/** Núcleo Fastchip (DDDs 61–69). */
export const TPCL_FASTCHIP_UFS = ['AC', 'DF', 'GO', 'MS', 'MT', 'RO', 'TO'] as const;

/** UFs adicionais aceites em contrato operacional / por entrega (`fastchip = false`). */
export const TPCL_ENTREGA_EXTRA_UFS = ['AM', 'AP', 'MA', 'PA', 'RR'] as const;

function sortUfsAlphabetically(ufs: readonly string[]): string[] {
  return [...ufs].sort((a, b) => a.localeCompare(b));
}

/** União núcleo + Norte (por entrega), ordem alfabética. */
export const TPCL_ENTREGA_UFS: readonly string[] = sortUfsAlphabetically([
  ...TPCL_FASTCHIP_UFS,
  ...TPCL_ENTREGA_EXTRA_UFS,
]);

export function formatTpclUfsSlash(mode: 'fastchip' | 'operational'): string {
  const ufs = mode === 'fastchip' ? TPCL_FASTCHIP_UFS : TPCL_ENTREGA_UFS;
  return sortUfsAlphabetically(ufs).join(' / ');
}

/** Valor persistido no select operacional TIM para TPCL por entrega. */
export const NON_FASTCHIP_TPCL_TOKEN_VALUE = `TPCL - ${formatTpclUfsSlash('operational')}`;

/** Rótulo do select / legenda para TPCL. */
export function formatTpclTokenLabel(mode: 'fastchip' | 'operational'): string {
  const ufs = formatTpclUfsSlash(mode);
  if (mode === 'fastchip') {
    return `TPCL — DDD 61–69 (${ufs})`;
  }
  return `TPCL — ${ufs}`;
}

/** UF e DDDs canónicos do cluster Bahia Fastchip (sem DDD 76/79). */
export const BA_FASTCHIP_UFS = ['BA'] as const;
export const BA_VALID_DDDS = ['71', '73', '74', '75', '77'] as const;

/** Cidades de referência por DDD (legenda operacional / importação). */
export const BA_DDD_REFERENCE_CITIES: Record<(typeof BA_VALID_DDDS)[number], string> = {
  '71': 'Salvador, Lauro de Freitas, Camaçari',
  '73': 'Ilhéus, Itabuna, Jequié',
  '74': 'Juazeiro, Jacobina, Senhor do Bonfim',
  '75': 'Feira de Santana, Alagoinhas, Santo Antônio de Jesus',
  '77': 'Vitória da Conquista, Barreiras, Luís Eduardo Magalhães',
};

export function formatBaClusterDddsList(): string {
  return BA_VALID_DDDS.join(', ');
}

export function formatBaClusterCitiesLegend(): string {
  return BA_VALID_DDDS.map((ddd) => `${ddd}: ${BA_DDD_REFERENCE_CITIES[ddd]}`).join(' · ');
}

export function formatBaTokenLabel(mode: 'fastchip' | 'operational'): string {
  if (mode === 'fastchip') {
    return `BA — DDD ${formatBaClusterDddsList()} (Bahia)`;
  }
  return 'BA — BA / SE — DDD 71, 73, 74, 75, 77, 79';
}

/** UF e DDDs canónicos do cluster HDM2 Fastchip (RS). */
export const HDM2_FASTCHIP_UFS = ['RS'] as const;
export const HDM2_VALID_DDDS = ['51', '52', '53', '54', '55'] as const;

/** Cidades de referência por DDD (legenda operacional / importação). */
export const HDM2_DDD_REFERENCE_CITIES: Record<(typeof HDM2_VALID_DDDS)[number], string> = {
  '51': 'Porto Alegre, Canoas, Gravataí',
  '52': 'Pelotas, Rio Grande',
  '53': 'Santa Maria, Bagé, São Gabriel',
  '54': 'Caxias do Sul, Bento Gonçalves, Passo Fundo',
  '55': 'Santa Cruz do Sul, Lajeado, Ijuí',
};

export function formatHdm2ClusterDddsList(): string {
  return HDM2_VALID_DDDS.join(', ');
}

export function formatHdm2ClusterCitiesLegend(): string {
  return HDM2_VALID_DDDS.map((ddd) => `${ddd}: ${HDM2_DDD_REFERENCE_CITIES[ddd]}`).join(' · ');
}

export function formatHdm2TokenLabel(mode: 'fastchip' | 'operational'): string {
  if (mode === 'fastchip') {
    return `HDM2 — DDD ${formatHdm2ClusterDddsList()} (RS)`;
  }
  return `HDM2 — RS — DDD ${formatHdm2ClusterDddsList()}`;
}

/** UFs e DDDs canónicos para exibição e inferência HB/CP (paridade operacional). */
export type SeniorClusterHbCp = typeof FASTCHIP_CLUSTER_HB | typeof FASTCHIP_CLUSTER_CP;

export const CLUSTER_DISPLAY: Record<SeniorClusterHbCp, { ufs: readonly string[]; ddds: readonly string[] }> = {
  HB: { ufs: ['PE', 'CE'], ddds: ['81', '85', '87', '88'] },
  CP: { ufs: ['AL', 'PB', 'PI', 'RN'], ddds: ['82', '83', '84', '86', '89'] },
};

export function formatClusterUfs(cluster: SeniorClusterHbCp): string {
  return CLUSTER_DISPLAY[cluster].ufs.join(' | ');
}

export function formatClusterDddsList(cluster: SeniorClusterHbCp): string {
  return [...CLUSTER_DISPLAY[cluster].ddds].join(', ');
}

/** Rótulo unificado para select/legenda (fastchip ou operacional TIM). */
export function formatClusterTokenLabel(
  cluster: SeniorClusterHbCp,
  mode: 'fastchip' | 'operational',
  operationalRegionLabel?: string
): string {
  const ddds = formatClusterDddsList(cluster);
  if (mode === 'fastchip') {
    const ufsLabel = formatClusterUfs(cluster);
    return `${cluster} — DDD ${ddds} (${ufsLabel})`;
  }
  const region = operationalRegionLabel ?? formatClusterUfs(cluster);
  return `${cluster} — ${region} — DDD ${ddds}`;
}
const TPCL_MARKET_ESTADOS = new Set<string>(TPCL_FASTCHIP_UFS);
const TPCL_ENTREGA_MARKET_ESTADOS = new Set<string>(TPCL_ENTREGA_UFS);

/** Valor legado do select operacional TIM (sem GO/MT no rótulo). */
export const LEGACY_NON_FASTCHIP_TPCL_TOKEN = 'TPCL - DF / AC / RO / MS / TO';

/** Valor intermédio (núcleo sem UFs Norte) — normalizado para {@link NON_FASTCHIP_TPCL_TOKEN_VALUE}. */
export const LEGACY_NON_FASTCHIP_TPCL_TOKEN_V2 = 'TPCL - DF / GO / TO / MT / MS / AC / RO';

export function isHbDdd(ddd: string | undefined | null): boolean {
  const d = (ddd ?? '').trim();
  return d.length > 0 && HB_DDDS.has(d);
}

export function isCpDdd(ddd: string | undefined | null): boolean {
  const d = (ddd ?? '').trim();
  return d.length > 0 && CP_DDDS.has(d);
}

export function isTpclDdd(ddd: string | undefined | null): boolean {
  const d = (ddd ?? '').trim();
  return d.length > 0 && TPCL_DDDS.has(d);
}

export function isBaDdd(ddd: string | undefined | null): boolean {
  const d = (ddd ?? '').trim();
  return d.length > 0 && BA_DDDS.has(d);
}

export function isHdm2Ddd(ddd: string | undefined | null): boolean {
  const d = (ddd ?? '').trim();
  return d.length > 0 && HDM2_DDDS.has(d);
}

export function isHbMarketEstado(estado: string | undefined | null): boolean {
  const e = (estado ?? '').trim().toUpperCase();
  return e.length > 0 && HB_MARKET_ESTADOS.has(e);
}

export function isCpMarketEstado(estado: string | undefined | null): boolean {
  const e = (estado ?? '').trim().toUpperCase();
  return e.length > 0 && CP_MARKET_ESTADOS.has(e);
}

export function isTpclMarketEstado(estado: string | undefined | null): boolean {
  const e = (estado ?? '').trim().toUpperCase();
  return e.length > 0 && TPCL_MARKET_ESTADOS.has(e);
}

export function isBaMarketEstado(estado: string | undefined | null): boolean {
  const e = (estado ?? '').trim().toUpperCase();
  return e.length > 0 && BA_MARKET_ESTADOS.has(e);
}

export function isHdm2MarketEstado(estado: string | undefined | null): boolean {
  const e = (estado ?? '').trim().toUpperCase();
  return e.length > 0 && HDM2_MARKET_ESTADOS.has(e);
}

/** UF mercado TPCL em contrato por entrega (núcleo + AM/AP/MA/PA/RR). */
export function isTpclEntregaMarketEstado(estado: string | undefined | null): boolean {
  const e = (estado ?? '').trim().toUpperCase();
  return e.length > 0 && TPCL_ENTREGA_MARKET_ESTADOS.has(e);
}

export function isSeniorClusterMarketEstado(estado: string | undefined | null): boolean {
  return (
    isHbMarketEstado(estado) ||
    isCpMarketEstado(estado) ||
    isTpclMarketEstado(estado) ||
    isBaMarketEstado(estado) ||
    isHdm2MarketEstado(estado)
  );
}

export function getHbValidDdds(): string[] {
  return Array.from(HB_DDDS).sort();
}

export function getCpValidDdds(): string[] {
  return Array.from(CP_DDDS).sort();
}

/** DDDs 81–89 (união HB e CP) para select de linha no mercado Nordeste. */
export function getHbCpUnionValidDdds(): string[] {
  return Array.from(HB_CP_UNION_DDDS).sort();
}

export function isHbCpUnionDdd(ddd: string | undefined | null): boolean {
  const d = (ddd ?? '').trim();
  return d.length > 0 && HB_CP_UNION_DDDS.has(d);
}

export function isHbCpMarketEstado(estado: string | undefined | null): boolean {
  return isHbMarketEstado(estado) || isCpMarketEstado(estado);
}

export function normalizeCityForClusterCompare(cidade: string): string {
  return cidade
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();
}

/** Cidade do cadastro do cliente ∈ cluster Guarapuava (DDD 42 → MH). */
export function isPrMhClusterCity(cidade: string | undefined | null): boolean {
  if (!cidade?.trim()) return false;
  return PR_MH_CLUSTER_CITIES.has(normalizeCityForClusterCompare(cidade));
}

export function getTpclValidDdds(): string[] {
  return Array.from(TPCL_DDDS).sort();
}

export function getBaValidDdds(): string[] {
  return Array.from(BA_DDDS).sort();
}

export function getHdm2ValidDdds(): string[] {
  return Array.from(HDM2_DDDS).sort();
}

/** Mercado E.R.2 GESTAO — pool ICCID SP (virada 29/09/2026). */
export const ER2_MARKET_ESTADOS = ['SP'] as const;
export const ER2_LINE_DDDS = ['11', '12', '13', '14', '15', '16', '17', '18', '19'] as const;

const ER2_MARKET_ESTADO_SET = new Set<string>(ER2_MARKET_ESTADOS);

export function isEr2MarketEstado(estado: string | undefined | null): boolean {
  const e = (estado ?? '').trim().toUpperCase();
  return e.length > 0 && ER2_MARKET_ESTADO_SET.has(e);
}

/** DDDs de linha Aditivo Fastchip no cluster E.R.2 (SP, 11–19). */
export function getEr2ValidDddsForFastchipLines(): string[] {
  return [...ER2_LINE_DDDS];
}

export function iccidBelongsToEr2Pool(
  estados: string[] | undefined,
  estadoLegado?: string | null
): boolean {
  const slots = [...(estados ?? []), ...(estadoLegado?.trim() ? [estadoLegado.trim()] : [])];
  return slots.some((e) => ER2_MARKET_ESTADO_SET.has(e.trim().toUpperCase()));
}

/** Filtro API para pool regional E.R.2 (paridade com job de stock RJ+MG+ES). */
export function getEr2IccidSearchEstadoFilter(): string {
  return 'SP';
}

export function formatEr2ClusterUiLabel(): string {
  return 'cluster E.R.2 (SP)';
}

export function formatEr2FastchipTokenLabel(): string {
  return 'E.R.2 GESTAO — DDD 11 a 19 (SP)';
}

/**
 * Mercado DH TELECOM — RJ/MG/ES.
 * Por entrega: sempre activo. Fastchip: padrão geográfico após a virada ER2→SP.
 */
export const DH_MARKET_ESTADOS = ['RJ', 'MG', 'ES'] as const;
/** DDDs Fastchip linha DH (MG). */
export const DH_LINE_DDDS = ['31', '32', '33', '34', '35', '37', '38'] as const;
/** DDDs operacionais TIM (espelho ER2 operacional: 21/22/24/27/28 + 31–38). */
export const DH_OPERATIONAL_DDDS = [
  '21',
  '22',
  '24',
  '27',
  '28',
  '31',
  '32',
  '33',
  '34',
  '35',
  '37',
  '38',
] as const;

const DH_MARKET_ESTADO_SET = new Set<string>(DH_MARKET_ESTADOS);
const DH_OPERATIONAL_DDD_SET = new Set<string>(DH_OPERATIONAL_DDDS);
const DH_LINE_DDD_SET = new Set<string>(DH_LINE_DDDS);

/** Valor persistido no select operacional TIM para DH por entrega. */
export const NON_FASTCHIP_DH_TOKEN_VALUE = 'DH - RJ/MG/ES';

/** Token operacional TIM para E.R.2 em SP (virada 29/09/2026). */
export const NON_FASTCHIP_ER2_SP_TOKEN_VALUE = 'ER2 - SP';

/** Início do lote ER2 de SP (romaneio 28/09). Chips SP anteriores são SH legado. */
export const ER2_SP_NEW_LOT_FROM = new Date('2026-09-28T00:00:00.000Z');

export function isDhMarketEstado(estado: string | undefined | null): boolean {
  const e = (estado ?? '').trim().toUpperCase();
  return e.length > 0 && DH_MARKET_ESTADO_SET.has(e);
}

/** Filtro API geográfico do mercado DH (RJ+MG+ES); combinar com `cluster=DH`. */
export function getDhIccidSearchEstadoFilter(): string {
  return 'RJ,MG,ES';
}

/** DDD operacional TIM do mercado DH (espelho ER2 operacional). */
export function isDhDdd(ddd: string | undefined | null): boolean {
  const d = (ddd ?? '').trim();
  return d.length > 0 && DH_OPERATIONAL_DDD_SET.has(d);
}

/** DDD de linha Aditivo Fastchip no mercado DH (espelho ER2 linha — MG). */
export function isDhLineDdd(ddd: string | undefined | null): boolean {
  const d = (ddd ?? '').trim();
  return d.length > 0 && DH_LINE_DDD_SET.has(d);
}

export function getDhValidDddsForFastchipLines(): string[] {
  return [...DH_LINE_DDDS];
}

export function getDhOperationalDdds(): string[] {
  return [...DH_OPERATIONAL_DDDS];
}

/** ICCID com cluster Senior DH em qualquer UF da linha. */
export function iccidHasDhCluster(
  estadosInfo: Array<{ cluster?: string | null }> | undefined | null
): boolean {
  if (!estadosInfo?.length) return false;
  return estadosInfo.some((e) => (e.cluster ?? '').trim().toUpperCase() === FASTCHIP_CLUSTER_DH);
}

export function formatDhClusterUiLabel(): string {
  return 'cluster DH (RJ/MG/ES)';
}

export function formatDhFastchipTokenLabel(): string {
  return `DH — DDD ${DH_LINE_DDDS.join(', ')} (RJ/MG/ES)`;
}

export function formatDhTokenLabel(mode: 'fastchip' | 'operational'): string {
  if (mode === 'fastchip') {
    return formatDhFastchipTokenLabel();
  }
  return `DH — RJ / MG / ES — DDD 21, 22, 24, 27, 28 e 31 a 38`;
}

/** Resultado do pool ICCID Fastchip para mercado RJ/MG/ES (flag-driven). */
export type RjMgEsFastchipIccidSearch = {
  /** Filtro geográfico multi-UF para a API (`RJ,MG,ES`). */
  estadoFilter: string;
  /** Cluster Senior na query; só presente quando Fastchip usa DH. */
  cluster?: typeof FASTCHIP_CLUSTER_DH;
  /** true → filtrar por `iccidHasDhCluster` e labels DH no fluxo Fastchip. */
  usesDhCluster: boolean;
};

/**
 * Resolve se o pool Fastchip RJ/MG/ES ativo é E.R.2 ou DH, dado o estado atual de
 * `fastchipClusterSettingsState` e a preferência de cluster do time do vendedor
 * (`null` = sem preferência). Espelha `FastchipClusterRules.ResolveRjMgEsFastchipSeniorToken` (API).
 */
export function resolveRjMgEsActiveCluster(
  teamClusterPreference?: 'ER2' | 'DH' | null
): 'ER2' | 'DH' {
  const { er2Enabled, dhEnabled, defaultCluster } = fastchipClusterSettingsState;
  if (dhEnabled && !er2Enabled) return 'DH';
  if (er2Enabled && !dhEnabled) return 'ER2';
  if (er2Enabled && dhEnabled) {
    if (teamClusterPreference === 'DH') return 'DH';
    if (teamClusterPreference === 'ER2') return 'ER2';
    return defaultCluster;
  }
  return 'DH';
}

/**
 * Pool ICCID Fastchip: SP → E.R.2 (lote novo); RJ/MG/ES → DH.
 * `null` se a UF não for desses mercados.
 */
export function resolveRjMgEsFastchipIccidSearch(
  estado: string | undefined | null,
  _teamClusterPreference?: 'ER2' | 'DH' | null
): RjMgEsFastchipIccidSearch | null {
  if (isEr2MarketEstado(estado)) {
    return {
      estadoFilter: getEr2IccidSearchEstadoFilter(),
      usesDhCluster: false,
    };
  }
  if (isDhMarketEstado(estado)) {
    return {
      estadoFilter: getDhIccidSearchEstadoFilter(),
      cluster: FASTCHIP_CLUSTER_DH,
      usesDhCluster: true,
    };
  }
  return null;
}

/** Rótulo UI do pool Fastchip: SP → E.R.2; RJ/MG/ES → DH. */
export function formatRjMgEsFastchipClusterUiLabel(estado?: string | null): string {
  if (isEr2MarketEstado(estado)) return formatEr2ClusterUiLabel();
  return formatDhClusterUiLabel();
}

export type SeniorTokenFromDdd =
  | 'MH'
  | 'E.R.GESTAO'
  | typeof FASTCHIP_CLUSTER_HB
  | typeof FASTCHIP_CLUSTER_CP
  | typeof FASTCHIP_CLUSTER_TPCL
  | typeof FASTCHIP_CLUSTER_BA
  | typeof FASTCHIP_CLUSTER_HDM2;

/** DDD da linha Aditivo → token Senior curto (HB, CP, TPCL, BA, HDM2, PR). */
export function inferSeniorClusterTokenFromAditivoDdd(
  ddd: string | undefined | null,
  cidadeCliente?: string | null
): SeniorTokenFromDdd | null {
  const d = (ddd ?? '').trim();
  if (!d) return null;
  if (isHbDdd(d)) return FASTCHIP_CLUSTER_HB;
  if (isCpDdd(d)) return FASTCHIP_CLUSTER_CP;
  if (isTpclDdd(d)) return FASTCHIP_CLUSTER_TPCL;
  if (isBaDdd(d)) return FASTCHIP_CLUSTER_BA;
  if (isHdm2Ddd(d)) return FASTCHIP_CLUSTER_HDM2;
  if (d === '42') return isPrMhClusterCity(cidadeCliente) ? 'MH' : 'E.R.GESTAO';
  if (d === '41' || d === '43' || d === '44' || d === '45') return 'E.R.GESTAO';
  return null;
}

export function iccidHasCluster(
  estadosInfo: { estado: string; cluster?: string | null }[] | undefined,
  cluster: string
): boolean {
  const c = cluster.trim().toUpperCase();
  return (estadosInfo ?? []).some((e) => (e.cluster ?? '').trim().toUpperCase() === c);
}

export function iccidHasHbCluster(
  estadosInfo: { estado: string; cluster?: string | null }[] | undefined
): boolean {
  return iccidHasCluster(estadosInfo, FASTCHIP_CLUSTER_HB);
}

export function iccidHasCpCluster(
  estadosInfo: { estado: string; cluster?: string | null }[] | undefined
): boolean {
  return iccidHasCluster(estadosInfo, FASTCHIP_CLUSTER_CP);
}

export function iccidHasTpclCluster(
  estadosInfo: { estado: string; cluster?: string | null }[] | undefined
): boolean {
  return iccidHasCluster(estadosInfo, FASTCHIP_CLUSTER_TPCL);
}

export function iccidHasBaCluster(
  estadosInfo: { estado: string; cluster?: string | null }[] | undefined
): boolean {
  return iccidHasCluster(estadosInfo, FASTCHIP_CLUSTER_BA);
}

export function iccidHasHdm2Cluster(
  estadosInfo: { estado: string; cluster?: string | null }[] | undefined
): boolean {
  return iccidHasCluster(estadosInfo, FASTCHIP_CLUSTER_HDM2);
}

function inferClusterFromLines<
  T extends
    | typeof FASTCHIP_CLUSTER_HB
    | typeof FASTCHIP_CLUSTER_CP
    | typeof FASTCHIP_CLUSTER_TPCL
    | typeof FASTCHIP_CLUSTER_BA
    | typeof FASTCHIP_CLUSTER_HDM2
>(
  cluster: T,
  linhas: { chipCcid?: string; ddd?: string }[],
  availableICcIds: { ccId: string; estadosInfo?: { estado: string; cluster?: string | null }[] }[],
  isDdd: (ddd: string | undefined | null) => boolean
): T | undefined {
  const firstWithCcid = linhas.find((l) => l.chipCcid?.trim());
  if (firstWithCcid?.chipCcid) {
    const ic = availableICcIds.find((i) => i.ccId === firstWithCcid.chipCcid);
    if (iccidHasCluster(ic?.estadosInfo, cluster)) return cluster;
  }
  const firstDdd = linhas.find((l) => (l.ddd ?? '').trim())?.ddd;
  if (firstDdd && isDdd(firstDdd)) return cluster;
  return undefined;
}

export function inferHbClusterFromLines(
  linhas: { chipCcid?: string; ddd?: string }[],
  availableICcIds: { ccId: string; estadosInfo?: { estado: string; cluster?: string | null }[] }[]
): typeof FASTCHIP_CLUSTER_HB | undefined {
  return inferClusterFromLines(FASTCHIP_CLUSTER_HB, linhas, availableICcIds, isHbDdd);
}

export function inferCpClusterFromLines(
  linhas: { chipCcid?: string; ddd?: string }[],
  availableICcIds: { ccId: string; estadosInfo?: { estado: string; cluster?: string | null }[] }[]
): typeof FASTCHIP_CLUSTER_CP | undefined {
  return inferClusterFromLines(FASTCHIP_CLUSTER_CP, linhas, availableICcIds, isCpDdd);
}

export function inferTpclClusterFromLines(
  linhas: { chipCcid?: string; ddd?: string }[],
  availableICcIds: { ccId: string; estadosInfo?: { estado: string; cluster?: string | null }[] }[]
): typeof FASTCHIP_CLUSTER_TPCL | undefined {
  return inferClusterFromLines(FASTCHIP_CLUSTER_TPCL, linhas, availableICcIds, isTpclDdd);
}

export function inferBaClusterFromLines(
  linhas: { chipCcid?: string; ddd?: string }[],
  availableICcIds: { ccId: string; estadosInfo?: { estado: string; cluster?: string | null }[] }[]
): typeof FASTCHIP_CLUSTER_BA | undefined {
  return inferClusterFromLines(FASTCHIP_CLUSTER_BA, linhas, availableICcIds, isBaDdd);
}

export function inferHdm2ClusterFromLines(
  linhas: { chipCcid?: string; ddd?: string }[],
  availableICcIds: { ccId: string; estadosInfo?: { estado: string; cluster?: string | null }[] }[]
): typeof FASTCHIP_CLUSTER_HDM2 | undefined {
  return inferClusterFromLines(FASTCHIP_CLUSTER_HDM2, linhas, availableICcIds, isHdm2Ddd);
}
